import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Typography, Button, Card, message } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { SubscriptionPlans } from '../components/subscription/SubscriptionPlans';
import { SubscriptionStatus } from '../components/subscription/SubscriptionStatus';
import { SubscriptionPlan } from '../types/subscription';


const { Content } = Layout;
const { Title, Text } = Typography;

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (plan.planType === 'FREE') {
      message.info('You are already on the free plan');
      return;
    }

    navigate('/subscription/payment', {
      state: {
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        currency: plan.currency || 'INR',
        billingCycle: (plan.billingCycle || 'monthly') as 'monthly' | 'annual',
        mode: 'new',
      },
    });
  };

  const features = [
    'Unlimited students and classes',
    'Interactive whiteboard',
    'Video conferencing',
    'Assignment management',
    'Progress tracking',
    '24/7 customer support',
  ];

  return (
    <Layout className="min-h-screen bg-gray-50">
      <Content className="p-4 md:p-8 max-w-6xl mx-auto w-full">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          Back to Dashboard
        </Button>

        <div className="text-center mb-8">
          <Title level={2} className="mb-2">Choose Your Plan</Title>
          <Text type="secondary" className="text-lg">
            Select the plan that works best for you
          </Text>
        </div>

        {/* Current Subscription Status */}
        <div className="mb-8">
          <SubscriptionStatus 
            onUpgradeClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        </div>

        {/* Features Comparison */}
        <Card className="mb-8">
          <Title level={4} className="text-center mb-6">Compare Features</Title>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-4">Features</th>
                  <th className="text-center p-4">Free</th>
                  <th className="text-center p-4">Basic</th>
                  <th className="text-center p-4">Premium</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-4">{feature}</td>
                    <td className="text-center p-4">
                      {index < 3 ? <CheckCircleOutlined className="text-green-500" /> : '--'}
                    </td>
                    <td className="text-center p-4">
                      {index < 5 ? <CheckCircleOutlined className="text-green-500" /> : '--'}
                    </td>
                    <td className="text-center p-4">
                      <CheckCircleOutlined className="text-green-500" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Plans */}
        <SubscriptionPlans onSelectPlan={handleSelectPlan} />

        {/* FAQ Section */}
        <div className="mt-16">
          <Title level={3} className="text-center mb-6">Frequently Asked Questions</Title>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                question: 'Can I change my plan later?',
                answer: 'Yes, you can upgrade or downgrade your plan at any time. Your subscription will be prorated accordingly.'
              },
              {
                question: 'Is there a free trial?',
                answer: 'Yes, we offer a 14-day free trial for all paid plans. No credit card is required to start the trial.'
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit cards including Visa, Mastercard, American Express, and Discover.'
              },
              {
                question: 'Can I cancel anytime?',
                answer: 'Yes, you can cancel your subscription at any time. Your subscription will remain active until the end of the current billing period.'
              },
            ].map((item, index) => (
              <Card key={index} className="shadow-sm">
                <Text strong className="block mb-2">{item.question}</Text>
                <Text type="secondary">{item.answer}</Text>
              </Card>
            ))}
          </div>
        </div>

      </Content>
    </Layout>
  );
};

export default SubscriptionPage;

